/**
 * TemplateSearchPicker
 *
 * Embeddable search + select component for choosing a template.
 * Shows format/scope badges, default star, loading skeletons, and empty state.
 */

import { MagnifyingGlassIcon, StarIcon } from '@heroicons/react/20/solid';
import { useCallback, useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import type {
  ReadTemplateSummary,
} from '@/services/api/generated/templatesV2Service';

import {
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_FORMAT_COLORS,
  TEMPLATE_SCOPE_COLORS,
} from '../constants';
import { useTemplateSearch, type UseTemplateSearchOptions } from './useTemplateSearch';

interface TemplateSearchPickerProps {
  /** Currently selected template ID */
  selectedId?: string | null;
  /** Called when user selects a template */
  onSelect: (template: ReadTemplateSummary) => void;
  /** Filter options passed to the search hook */
  searchOptions?: UseTemplateSearchOptions;
  /** Optional className */
  className?: string;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Maximum height for the scrollable list */
  maxHeight?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

export const TemplateSearchPicker = ({
  selectedId,
  onSelect,
  searchOptions,
  className,
  searchPlaceholder = 'Search templates...',
  maxHeight = '320px',
  disabled = false,
}: TemplateSearchPickerProps) => {
  const {
    searchInput,
    onSearchChange,
    templates,
    totalCount,
    isLoading,
    isFetching,
  } = useTemplateSearch(searchOptions);

  const { data: firmsData } = useGetFirmsQuery({});
  const firmNameById = useMemo(() => {
    if (!firmsData) return {};
    return Object.fromEntries(firmsData.map((f) => [f.id, f.name]));
  }, [firmsData]);

  const handleSelect = useCallback(
    (template: ReadTemplateSummary) => {
      if (!disabled) {
        onSelect(template);
      }
    },
    [onSelect, disabled],
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Search input */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <Input
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-8 h-9 text-sm"
          disabled={disabled}
        />
      </div>

      {/* Results count */}
      {!isLoading && totalCount > 0 && (
        <p className="text-xs font-medium text-zinc-500 px-1">
          {totalCount} template{totalCount !== 1 ? 's' : ''}
          {isFetching && ' ...'}
        </p>
      )}

      {/* Template list */}
      <div
        className="overflow-y-auto rounded-md border border-zinc-200 dark:border-zinc-700"
        style={{ maxHeight }}
      >
        {isLoading ? (
          <PickerSkeleton />
        ) : templates.length === 0 ? (
          <PickerEmptyState hasSearch={!!searchInput} />
        ) : (
          <ul role="listbox" className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {templates.map((template) => (
              <TemplateRow
                key={template.id as string}
                template={template}
                isSelected={selectedId === (template.id as string)}
                onSelect={handleSelect}
                disabled={disabled}
                firmNameById={firmNameById}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ---------- Sub-components ---------- */

interface TemplateRowProps {
  template: ReadTemplateSummary;
  isSelected: boolean;
  onSelect: (template: ReadTemplateSummary) => void;
  disabled: boolean;
  firmNameById: Record<string, string>;
}

const formatShortDate = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TemplateRow = ({
  template,
  isSelected,
  onSelect,
  disabled,
  firmNameById,
}: TemplateRowProps) => {
  const categoryLabel =
    TEMPLATE_CATEGORY_LABELS[template.category] ??
    template.category;

  const firmName =
    template.scope === 'FIRM' && template.firm_id
      ? firmNameById[template.firm_id as string]
      : undefined;

  return (
    <li
      role="option"
      aria-selected={isSelected}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors',
        'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
        isSelected &&
          'bg-indigo-50 ring-1 ring-inset ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/30',
        disabled && 'pointer-events-none opacity-50',
      )}
      onClick={() => onSelect(template)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(template);
        }
      }}
    >
      {/* Default star */}
      {template.is_default && (
        <StarIcon
          className="h-4 w-4 flex-shrink-0 text-amber-500"
          aria-label="Default template"
        />
      )}

      {/* Template info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
          {template.display_name || template.name}
        </p>
        {firmName && (
          <p className="text-xs font-medium text-zinc-500 truncate">
            {firmName}
          </p>
        )}
        <p className="text-xs font-medium text-zinc-500 truncate">
          {categoryLabel}
          {template.version > 1 && ` v${template.version}`}
        </p>
      </div>

      {/* Date + Badges */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-xs font-medium text-zinc-500">
          {formatShortDate(template.created_at)}
        </span>
        <FormatBadge format={template.format} />
        <ScopeBadge scope={template.scope} />
      </div>
    </li>
  );
};

const FormatBadge = ({ format }: { format: string }) => {
  const colors =
    TEMPLATE_FORMAT_COLORS[format] ?? 'bg-zinc-100 text-zinc-600';
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase',
        colors,
      )}
    >
      {format}
    </span>
  );
};

const ScopeBadge = ({ scope }: { scope: string }) => {
  const colors =
    TEMPLATE_SCOPE_COLORS[scope] ?? 'bg-zinc-100 text-zinc-600';
  return (
    <span
      className={cn(
        'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold',
        colors,
      )}
    >
      {scope === 'GLOBAL' ? 'Global' : 'Firm'}
    </span>
  );
};

const PickerSkeleton = () => (
  <div className="space-y-0 divide-y divide-zinc-100 dark:divide-zinc-800">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-3 py-2.5">
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <div className="flex gap-1.5">
          <Skeleton className="h-4 w-10 rounded" />
          <Skeleton className="h-4 w-10 rounded" />
        </div>
      </div>
    ))}
  </div>
);

const PickerEmptyState = ({ hasSearch }: { hasSearch: boolean }) => (
  <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
    <MagnifyingGlassIcon className="h-8 w-8 text-zinc-300 mb-2" />
    <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
      {hasSearch ? 'No templates match your search' : 'No templates available'}
    </p>
    {hasSearch && (
      <p className="text-xs font-medium text-zinc-500 mt-1">
        Try a different search term
      </p>
    )}
  </div>
);
